import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { RootState } from '../store/store';
import { Transfer, Driver, VehicleType, TransferStatus, UserRole, TripType } from '../types';
import { transfersService } from '../services/transfersService';
import { driversService } from '../services/driversService';
import { vehicleTypesService } from '../services/vehicleTypesService';
import { toast } from 'react-toastify';
import { format } from 'date-fns';

type Tab = 'pool' | 'mine';
type AssignmentMode = 'select' | 'external';

interface AssignmentFormData {
  providerDriverId: string;
  providerVehicleTypeId: string;
  providerDriverName: string;
  providerDriverPhone: string;
  providerPlate: string;
  providerExternalVehicleType: string;
}

const ProviderTransfersPage: React.FC = () => {
  const { t } = useTranslation();
  const user = useSelector((state: RootState) => state.auth.user);
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState<Tab>('pool');
  const [poolTransfers, setPoolTransfers] = useState<Transfer[]>([]);
  const [myTransfers, setMyTransfers] = useState<Transfer[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [vehicleTypes, setVehicleTypes] = useState<VehicleType[]>([]);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState<string | null>(null);

  const [selectedTransfer, setSelectedTransfer] = useState<Transfer | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignmentMode, setAssignmentMode] = useState<AssignmentMode>('select');
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState<AssignmentFormData>({
    providerDriverId: '',
    providerVehicleTypeId: '',
    providerDriverName: '',
    providerDriverPhone: '',
    providerPlate: '',
    providerExternalVehicleType: '',
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [confirmTransfer, setConfirmTransfer] = useState<Transfer | null>(null);
  const [rejectTransfer, setRejectTransfer] = useState<Transfer | null>(null);

  const DISMISSED_KEY = `dismissed_pool_${user?.providerId || 'unknown'}`;
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem(DISMISSED_KEY) || '[]')); } catch { return new Set(); }
  });

  const handleDismiss = (id: string) => {
    const next = new Set(dismissedIds);
    next.add(id);
    setDismissedIds(next);
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(Array.from(next)));
    setRejectTransfer(null);
    toast.success('Transfer reddedildi.');
  };

  const requestReject = (transfer: Transfer, e: React.MouseEvent) => {
    e.stopPropagation();
    setRejectTransfer(transfer);
  };

  const isProviderUser = user?.role === UserRole.PROVIDER_USER || user?.role === UserRole.PROVIDER_MANAGER || user?.role === UserRole.POOL_MEMBER;

  const isPoolMember = user?.role === UserRole.POOL_MEMBER;

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [pool, mine] = await Promise.all([
        transfersService.getPool(),
        transfersService.getAll({ limit: '200' }),
      ]);
      setPoolTransfers(pool);
      setMyTransfers(Array.isArray(mine) ? mine : (mine.data ?? []));

      if (!isPoolMember) {
        const [driversData, vehicleTypesData] = await Promise.all([
          driversService.getAll(true),
          vehicleTypesService.getAll(true),
        ]);
        setDrivers(driversData.filter(d => d.providerId === user?.providerId));
        setVehicleTypes(vehicleTypesData.filter(vt => vt.providerId === user?.providerId));
      }
    } catch {
      toast.error(t('transfers.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [user?.providerId, isPoolMember, t]);

  useEffect(() => {
    if (isProviderUser) loadData();
  }, [isProviderUser, loadData]);

  // Auto-open transfer detail from notification link (?id=xxx)
  useEffect(() => {
    const transferId = searchParams.get('id');
    if (transferId) {
      searchParams.delete('id');
      setSearchParams(searchParams, { replace: true });
      transfersService.getById(transferId)
        .then(transfer => {
          setSelectedTransfer(transfer);
          setShowDetailModal(true);
        })
        .catch(() => {});
    }
  }, [searchParams, setSearchParams]);

  const handleClaim = async (transfer: Transfer, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setClaiming(transfer.id);
    setConfirmTransfer(null);
    try {
      await transfersService.claimTransfer(transfer.id);
      toast.success('Transfer başarıyla seçildi!');
      await loadData();
      setActiveTab('mine');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Transfer seçilemedi, başka bir tedarikçi almış olabilir.');
    } finally {
      setClaiming(null);
    }
  };

  const requestClaim = (transfer: Transfer, e: React.MouseEvent) => {
    e.stopPropagation();
    setConfirmTransfer(transfer);
  };

  const handleRowClick = (transfer: Transfer) => {
    setSelectedTransfer(transfer);
    setShowDetailModal(true);
  };

  const handleOpenAssignModal = () => {
    if (!selectedTransfer) return;
    setFormData({
      providerDriverId: selectedTransfer.providerDriverId || '',
      providerVehicleTypeId: selectedTransfer.providerVehicleTypeId || '',
      providerDriverName: selectedTransfer.providerDriverName || '',
      providerDriverPhone: selectedTransfer.providerDriverPhone || '',
      providerPlate: selectedTransfer.providerPlate || '',
      providerExternalVehicleType: selectedTransfer.providerExternalVehicleType || '',
    });
    setAssignmentMode(
      selectedTransfer.providerDriverId || selectedTransfer.providerVehicleTypeId ? 'select'
      : selectedTransfer.providerDriverName || selectedTransfer.providerPlate ? 'external'
      : drivers.length > 0 ? 'select' : 'external'
    );
    setShowDetailModal(false);
    setShowAssignModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTransfer) return;
    try {
      setSaving(true);
      const updateData: any = {};
      if (assignmentMode === 'select') {
        const selectedDriver = drivers.find(d => d.id === formData.providerDriverId);
        const selectedVehicle = vehicleTypes.find(v => v.id === formData.providerVehicleTypeId);
        updateData.providerDriverId = formData.providerDriverId || null;
        updateData.providerVehicleTypeId = formData.providerVehicleTypeId || null;
        updateData.providerDriverName = selectedDriver?.fullName || '';
        updateData.providerDriverPhone = selectedDriver?.phone || '';
        updateData.providerPlate = selectedVehicle?.licensePlate || '';
        updateData.providerExternalVehicleType = selectedVehicle?.name || '';
      } else {
        updateData.providerDriverName = formData.providerDriverName;
        updateData.providerDriverPhone = formData.providerDriverPhone;
        updateData.providerPlate = formData.providerPlate;
        updateData.providerExternalVehicleType = formData.providerExternalVehicleType;
        updateData.providerDriverId = null;
        updateData.providerVehicleTypeId = null;
      }
      await transfersService.update(selectedTransfer.id, updateData);
      toast.success('Araç/şoför ataması kaydedildi');
      setShowAssignModal(false);
      setSelectedTransfer(null);
      loadData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Atama kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status: TransferStatus) => {
    const config: Record<TransferStatus, { bg: string; text: string; label: string }> = {
      [TransferStatus.AVAILABLE]: { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-800 dark:text-emerald-300', label: 'Mevcut' },
      [TransferStatus.CLAIMED]:   { bg: 'bg-yellow-100 dark:bg-yellow-900/30',   text: 'text-yellow-800 dark:text-yellow-300',   label: 'Seçildi' },
      [TransferStatus.CONFIRMED]: { bg: 'bg-blue-100 dark:bg-blue-900/30',       text: 'text-blue-800 dark:text-blue-300',       label: 'Onaylandı' },
      [TransferStatus.IN_PROGRESS]:{ bg: 'bg-purple-100 dark:bg-purple-900/30',  text: 'text-purple-800 dark:text-purple-300',   label: 'Devam Ediyor' },
      [TransferStatus.COMPLETED]: { bg: 'bg-green-100 dark:bg-green-900/30',     text: 'text-green-800 dark:text-green-300',     label: 'Tamamlandı' },
      [TransferStatus.CANCELLED]: { bg: 'bg-red-100 dark:bg-red-900/30',         text: 'text-red-800 dark:text-red-300',         label: 'İptal' },
    };
    const c = config[status] ?? config[TransferStatus.AVAILABLE];
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${c.bg} ${c.text}`}>
        {c.label}
      </span>
    );
  };

  const getTripTypeBadge = (tripType?: TripType) => {
    if (tripType === TripType.ARRIVAL) return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">Geliş</span>;
    if (tripType === TripType.DEPARTURE) return <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">Dönüş</span>;
    return <span className="text-gray-400">-</span>;
  };

  const filterBySearch = (list: Transfer[]) =>
    list.filter(t => {
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      return (
        t.pickupLocation?.toLowerCase().includes(q) ||
        t.dropoffLocation?.toLowerCase().includes(q) ||
        t.transferCode?.toLowerCase().includes(q) ||
        t.flightCode?.toLowerCase().includes(q)
      );
    });

  const visiblePool = filterBySearch(poolTransfers).filter(t => !dismissedIds.has(t.id));
  const visibleMine = filterBySearch(myTransfers);

  const TransferCard = ({ transfer, showClaim }: { transfer: Transfer; showClaim?: boolean }) => (
    <div
      onClick={() => handleRowClick(transfer)}
      className="p-4 sm:p-5 cursor-pointer transition-all duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/40 hover:shadow-sm active:scale-[0.995]"
    >
      <div className="flex gap-4 items-start">
        {/* Sol: tutar + tip */}
        <div className="flex-shrink-0 text-right min-w-[72px]">
          {transfer.providerPrice != null ? (
            <p className="text-lg font-bold text-green-600 dark:text-green-400 leading-tight">
              {Number(transfer.providerPrice).toFixed(0)}
              <span className="text-xs font-medium ml-0.5">{transfer.currency || 'TL'}</span>
            </p>
          ) : (
            <p className="text-sm text-gray-400">-</p>
          )}
          <div className="mt-1">{getTripTypeBadge(transfer.tripType)}</div>
        </div>

        {/* Sağ: tüm bilgiler */}
        <div className="flex-1 min-w-0">
          {/* Tarih + saat */}
          <p className="text-sm font-semibold text-gray-900 dark:text-white">
            {format(new Date(transfer.transferDate), 'dd.MM.yyyy')}
            <span className="text-gray-400 dark:text-gray-500 font-normal ml-2">{transfer.transferTime?.substring(0, 5)}</span>
          </p>

          {/* Güzergah */}
          <div className="mt-1 text-sm text-gray-700 dark:text-gray-200 leading-snug">
            <span className="font-medium truncate block">{transfer.pickupLocation}</span>
            <span className="text-gray-400 text-xs">↓</span>
            <span className="font-medium truncate block">{transfer.dropoffLocation}</span>
          </div>

          {/* Alt bilgiler */}
          <div className="mt-2 flex flex-wrap gap-2 text-xs text-gray-500 dark:text-gray-400">
            {transfer.passengerCount && (
              <span className="bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full">👥 {transfer.passengerCount} kişi</span>
            )}
            {transfer.flightCode && (
              <span className="bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full">✈ {transfer.flightCode}</span>
            )}
            {transfer.vehicleType && (
              <span className="bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full">🚗 {transfer.vehicleType}</span>
            )}
          </div>

          {/* Transfer kodu — alt */}
          <p className="mt-2 text-xs text-gray-400 dark:text-gray-500 font-mono">{transfer.transferCode}</p>
        </div>

        {/* Aksiyon butonları */}
        {showClaim && (
          <div className="flex-shrink-0 flex flex-col gap-2 ml-1">
            <button
              onClick={(e) => requestClaim(transfer, e)}
              disabled={claiming === transfer.id}
              className="min-w-[88px] px-3 py-2 bg-green-600 text-white text-sm font-semibold rounded-lg hover:bg-green-700 active:bg-green-800 disabled:opacity-50 transition-all duration-150 shadow-sm hover:shadow"
            >
              {claiming === transfer.id ? '...' : 'Seç'}
            </button>
            <button
              onClick={(e) => requestReject(transfer, e)}
              className="min-w-[88px] px-3 py-2 text-red-600 dark:text-red-400 border border-red-300 dark:border-red-700 text-sm font-semibold rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 active:bg-red-100 transition-all duration-150"
            >
              Reddet
            </button>
          </div>
        )}
      </div>
    </div>
  );

  if (!isProviderUser) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{t('errors.accessDenied')}</h2>
          <p className="text-gray-600 dark:text-gray-400">Bu sayfa sadece tedarikçi kullanıcıları içindir.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl text-gray-600 dark:text-gray-400">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
    <div className="max-w-2xl mx-auto px-4 py-4 sm:py-6">
      {/* Header */}
      <div className="mb-5">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Transferler</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Mevcut transferleri görüntüleyin ve seçin</p>
      </div>

      {/* Search */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Güzergah, kod veya uçuş kodu ara..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent shadow-sm text-sm"
        />
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-gray-700 mb-4">
        <button
          onClick={() => setActiveTab('pool')}
          className={`px-5 py-3 text-sm font-semibold border-b-2 transition-all duration-150 ${
            activeTab === 'pool'
              ? 'border-green-500 text-green-600 dark:text-green-400'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Mevcut Transferler
          {visiblePool.length > 0 && (
            <span className={`ml-2 text-xs px-2 py-0.5 rounded-full font-medium ${
              activeTab === 'pool'
                ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
            }`}>
              {visiblePool.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('mine')}
          className={`px-5 py-3 text-sm font-semibold border-b-2 transition-all duration-150 ${
            activeTab === 'mine'
              ? 'border-green-500 text-green-600 dark:text-green-400'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Transferlerim
          {visibleMine.length > 0 && (
            <span className={`ml-2 text-xs px-2 py-0.5 rounded-full font-medium ${
              activeTab === 'mine'
                ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
            }`}>
              {visibleMine.length}
            </span>
          )}
        </button>
      </div>

      {/* Content */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700 overflow-hidden">
        {activeTab === 'pool' && (
          visiblePool.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-4xl mb-3">🎉</div>
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">Şu an mevcut transfer yok</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Yeni transfer eklendiğinde burada görünecek</p>
            </div>
          ) : (
            visiblePool.map(transfer => (
              <TransferCard key={transfer.id} transfer={transfer} showClaim />
            ))
          )
        )}

        {activeTab === 'mine' && (
          visibleMine.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-4xl mb-3">📋</div>
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">Henüz transfer seçmediniz</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Mevcut Transferler sekmesinden transfer seçebilirsiniz</p>
            </div>
          ) : (
            visibleMine.map(transfer => (
              <TransferCard key={transfer.id} transfer={transfer} showClaim={false} />
            ))
          )
        )}
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedTransfer && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => { setShowDetailModal(false); setSelectedTransfer(null); }} />
            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-start mb-6">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">Transfer Detayı</h2>
                  <button onClick={() => { setShowDetailModal(false); setSelectedTransfer(null); }} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">ID</p>
                      <p className="font-mono text-sm font-medium text-gray-900 dark:text-white">{selectedTransfer.transferCode || selectedTransfer.id.substring(0, 8)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Durum</p>
                      {getStatusBadge(selectedTransfer.status)}
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Tarih</p>
                      <p className="font-medium text-gray-900 dark:text-white">{format(new Date(selectedTransfer.transferDate), 'dd.MM.yyyy')}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Saat</p>
                      <p className="font-medium text-gray-900 dark:text-white">{selectedTransfer.transferTime?.substring(0, 5)}</p>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Kalkış</p>
                    <p className="font-medium text-gray-900 dark:text-white">{selectedTransfer.pickupLocation}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Varış</p>
                    <p className="font-medium text-gray-900 dark:text-white">{selectedTransfer.dropoffLocation}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {selectedTransfer.flightCode && (
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Uçuş Kodu</p>
                        <p className="font-medium text-gray-900 dark:text-white">{selectedTransfer.flightCode}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Yolcu Sayısı</p>
                      <p className="font-medium text-gray-900 dark:text-white">{selectedTransfer.passengerCount || '-'}</p>
                    </div>
                    {selectedTransfer.vehicleType && (
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Araç Tipi</p>
                        <p className="font-medium text-gray-900 dark:text-white">{selectedTransfer.vehicleType}</p>
                      </div>
                    )}
                    {selectedTransfer.providerPrice && (
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Ücret</p>
                        <p className="font-semibold text-blue-600 dark:text-blue-400">{selectedTransfer.providerPrice} {selectedTransfer.currency}</p>
                      </div>
                    )}
                  </div>

                  {selectedTransfer.returnTime && (
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Dönüş Saati</p>
                      <p className="font-medium text-gray-900 dark:text-white">{selectedTransfer.returnTime?.substring(0, 5)}</p>
                    </div>
                  )}

                  {selectedTransfer.specialNotes && (
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Notlar</p>
                      <p className="font-medium text-gray-900 dark:text-white whitespace-pre-wrap">{selectedTransfer.specialNotes}</p>
                    </div>
                  )}

                  {selectedTransfer.guests && selectedTransfer.guests.length > 0 && (
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Misafirler</p>
                      <div className="space-y-2">
                        {selectedTransfer.guests.map((guest, idx) => (
                          <div key={idx} className="bg-gray-50 dark:bg-gray-700 p-3 rounded">
                            <p className="font-medium text-gray-900 dark:text-white">{guest.name}</p>
                            {guest.phone && <p className="text-sm text-gray-600 dark:text-gray-400">{guest.phone}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedTransfer.assignedProviderId === user?.providerId && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                      <h3 className="font-semibold text-blue-900 dark:text-blue-300 mb-2">Atama Bilgisi</h3>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <p className="text-blue-700 dark:text-blue-400">Şoför</p>
                          <p className="font-medium text-blue-900 dark:text-blue-200">{selectedTransfer.providerDriverName || selectedTransfer.providerDriver?.fullName || 'Atanmadı'}</p>
                        </div>
                        <div>
                          <p className="text-blue-700 dark:text-blue-400">Plaka</p>
                          <p className="font-medium text-blue-900 dark:text-blue-200">{selectedTransfer.providerPlate || '-'}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-6 flex gap-3">
                  {selectedTransfer.status === TransferStatus.AVAILABLE && (
                    <button
                      onClick={(e) => { setShowDetailModal(false); requestClaim(selectedTransfer, e); }}
                      disabled={claiming === selectedTransfer.id}
                      className="flex-1 bg-blue-600 text-white px-4 py-2.5 rounded-lg hover:bg-blue-700 font-medium transition-colors min-h-[44px] disabled:opacity-50"
                    >
                      {claiming === selectedTransfer.id ? 'Alınıyor...' : 'Transferi Seç'}
                    </button>
                  )}
                  {!isPoolMember &&
                    selectedTransfer.assignedProviderId === user?.providerId &&
                    selectedTransfer.status !== TransferStatus.COMPLETED &&
                    selectedTransfer.status !== TransferStatus.CANCELLED && (
                    <button
                      onClick={handleOpenAssignModal}
                      className="flex-1 bg-green-600 text-white px-4 py-2.5 rounded-lg hover:bg-green-700 font-medium transition-colors min-h-[44px]"
                    >
                      Şoför / Araç Ata
                    </button>
                  )}
                  <button
                    onClick={() => { setShowDetailModal(false); setSelectedTransfer(null); }}
                    className="flex-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-4 py-2.5 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 font-medium transition-colors min-h-[44px]"
                  >
                    Kapat
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Claim Modal */}
      {confirmTransfer && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => setConfirmTransfer(null)} />
            <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-sm w-full p-6">
              <div className="text-center mb-5">
                <div className="text-4xl mb-3">🤝</div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">Transferi Seçmek İstiyor musunuz?</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {confirmTransfer.pickupLocation} → {confirmTransfer.dropoffLocation}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {format(new Date(confirmTransfer.transferDate), 'dd.MM.yyyy')} — {confirmTransfer.transferTime?.substring(0, 5)}
                </p>
                {confirmTransfer.providerPrice != null && (
                  <p className="mt-3 text-lg font-bold text-blue-600 dark:text-blue-400">
                    {confirmTransfer.providerPrice} {confirmTransfer.currency || 'EUR'}
                  </p>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmTransfer(null)}
                  className="flex-1 px-4 py-2.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 font-medium transition-colors"
                >
                  Vazgeç
                </button>
                <button
                  onClick={() => handleClaim(confirmTransfer)}
                  disabled={claiming === confirmTransfer.id}
                  className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors disabled:opacity-50"
                >
                  {claiming === confirmTransfer.id ? 'Alınıyor...' : 'Evet, Seç'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject Confirm Dialog */}
      {rejectTransfer && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => setRejectTransfer(null)} />
            <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-sm w-full p-6">
              <div className="text-center mb-5">
                <div className="text-4xl mb-3">❌</div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">Transferi Reddetmek İstiyor musunuz?</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {rejectTransfer.pickupLocation} → {rejectTransfer.dropoffLocation}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {format(new Date(rejectTransfer.transferDate), 'dd.MM.yyyy')} — {rejectTransfer.transferTime?.substring(0, 5)}
                </p>
                <p className="text-xs text-gray-400 mt-2">Bu transfer listenizden kaldırılacak.</p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setRejectTransfer(null)}
                  className="flex-1 px-4 py-2.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 font-medium"
                >
                  Vazgeç
                </button>
                <button
                  onClick={() => handleDismiss(rejectTransfer.id)}
                  className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
                >
                  Evet, Reddet
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assignment Modal */}
      {showAssignModal && selectedTransfer && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => { setShowAssignModal(false); setSelectedTransfer(null); }} />
            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">Araç / Şoför Ata</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                {selectedTransfer.transferCode} — {selectedTransfer.pickupLocation} → {selectedTransfer.dropoffLocation}
              </p>

              <div className="flex mb-6 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                <button type="button" onClick={() => setAssignmentMode('select')}
                  className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${assignmentMode === 'select' ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm' : 'text-gray-600 dark:text-gray-400'}`}
                  disabled={drivers.length === 0 && vehicleTypes.length === 0}
                >
                  Listeden Seç
                </button>
                <button type="button" onClick={() => setAssignmentMode('external')}
                  className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${assignmentMode === 'external' ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm' : 'text-gray-600 dark:text-gray-400'}`}
                >
                  Harici Giriş
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {assignmentMode === 'select' ? (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Şoför Seç</label>
                      <select value={formData.providerDriverId} onChange={(e) => setFormData(p => ({ ...p, providerDriverId: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500">
                        <option value="">-- Şoför Seç --</option>
                        {drivers.map(d => <option key={d.id} value={d.id}>{d.fullName} - {d.phone}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Araç Seç</label>
                      <select value={formData.providerVehicleTypeId} onChange={(e) => setFormData(p => ({ ...p, providerVehicleTypeId: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500">
                        <option value="">-- Araç Seç --</option>
                        {vehicleTypes.map(v => <option key={v.id} value={v.id}>{v.name} {v.licensePlate ? `(${v.licensePlate})` : ''}</option>)}
                      </select>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Şoför Adı</label>
                      <input type="text" value={formData.providerDriverName} onChange={(e) => setFormData(p => ({ ...p, providerDriverName: e.target.value }))} placeholder="Şoför adı" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Telefon</label>
                      <input type="tel" value={formData.providerDriverPhone} onChange={(e) => setFormData(p => ({ ...p, providerDriverPhone: e.target.value }))} placeholder="Telefon numarası" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Plaka</label>
                      <input type="text" value={formData.providerPlate} onChange={(e) => setFormData(p => ({ ...p, providerPlate: e.target.value }))} placeholder="Araç plakası" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Araç Tipi</label>
                      <input type="text" value={formData.providerExternalVehicleType} onChange={(e) => setFormData(p => ({ ...p, providerExternalVehicleType: e.target.value }))} placeholder="Sedan, Minibüs..." className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500" />
                    </div>
                  </>
                )}
                <div className="flex justify-end gap-3 pt-4">
                  <button type="button" onClick={() => { setShowAssignModal(false); setSelectedTransfer(null); }} className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                    {t('common.cancel')}
                  </button>
                  <button type="submit" disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50">
                    {saving ? t('common.saving') : t('common.save')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
};

export default ProviderTransfersPage;
